import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from './mapSlice';
import { markersSlice } from '../../store/markersSlice';
import { UnitModal, UnitSubmitData, EditableUnit } from '../unit/UnitModal';
import { MapEntity } from './mapTypes.d';
import * as markerService from '../../services/markerService';

interface MapMarkerEditTriggerProps {
    /** Marker to edit; `null` keeps the modal hidden. */
    marker: MapEntity | null;
    /** Callback invoked to dismiss the modal (success or cancel). */
    onHide: () => void;
}

/**
 * Renders the unified UnitModal in edit mode for an annotation marker
 * selected on the map. On submit, persists the change via
 * `markerService.updateMarker` (PATCH) and optimistically upserts the
 * updated marker into the local Redux store. The MQTT echo from
 * `MarkersManager` is harmless because `markersSlice.addMarker` is an
 * idempotent upsert.
 *
 * Mirrors the shape of `MapUnitModalTrigger` so the create/edit pair
 * reads as a unit.
 */
export function MapMarkerEditTrigger({ marker, onHide }: MapMarkerEditTriggerProps) {
    const { eventId } = useParams<{ eventId: string }>();
    const dispatch = useAppDispatch();

    const editingUnit = useMemo<EditableUnit | null>(() => {
        if (!marker) return null;
        // A marker's identity is its `name` — the single stored identity
        // field, seeded here so the operator can edit it instead of retyping
        // it as a text modifier.
        return {
            id: marker.id,
            name: marker.name,
            sidc: marker.code,
            markerProperties: marker.markerProperties,
        };
    }, [marker]);

    const handleSubmit = useCallback(async (data: UnitSubmitData) => {
        if (!marker || !eventId) return;

        try {
            // Strip the derived overlays (`dtg` from position_time,
            // `uniqueDesignation` from the entity name) before persisting —
            // neither is a real text modifier. Matches `MapUnitModalTrigger`.
            const {
                dtg: _ignoredDtg,
                uniqueDesignation: _ignoredName,
                ...textModifiers
            } = (data.markerProperties ?? {}) as Record<string, unknown>;

            await markerService.updateMarker(eventId, marker.id, {
                name: data.name,
                sidc: data.sidc,
                text_modifiers: textModifiers,
            });

            // Optimistic local upsert. The MQTT echo from MarkersManager
            // (if backend publishes on PATCH) will land on the same
            // idempotent reducer, so the double-fire is harmless.
            dispatch(markersSlice.actions.addMarker({
                ...marker,
                name: data.name,
                code: data.sidc,
                markerProperties: data.markerProperties,
            }));

            onHide();
        } catch (error) {
            console.error('Failed to update marker:', error);
        }
    }, [marker, eventId, dispatch, onHide]);

    return (
        <UnitModal
            show={!!marker}
            onHide={onHide}
            context="map"
            mode="edit"
            editingUnit={editingUnit}
            showNameField={true}
            showTextModifiers={true}
            onSubmit={handleSubmit}
        />
    );
}

export default MapMarkerEditTrigger;
